// components/Modals/InstagramImportModal/InstagramImportModal.js
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { instagramScrapeAPI } from '../../../services/api';
import './InstagramImportModal.css';

const TEMPLATE_PROMPTS = [
  {
    id: 'background_change',
    label: '🌆 Arka Plan Değiştir',
    prompt: 'Keep the same person, face, hair and pose exactly the same. Change the background to: ',
    needsInput: true,
    placeholder: 'Örn: tropical beach with sunset, futuristic neon city...'
  },
  {
    id: 'outfit_change',
    label: '👔 Kıyafet Değiştir',
    prompt: 'Keep the same person, face, hair and pose exactly the same. Change the outfit to: ',
    needsInput: true,
    placeholder: 'Örn: black suit with red tie, casual summer dress...'
  },
  {
    id: 'style_change',
    label: '🎨 Stil Değiştir',
    prompt: 'Keep the same person and composition. Transform the image style to: ',
    needsInput: true,
    placeholder: 'Örn: anime style, oil painting, watercolor, pixel art...'
  },
  {
    id: 'custom',
    label: '✏️ Özel Prompt',
    prompt: '',
    needsInput: true,
    placeholder: 'Kendi prompt\'unuzu yazın...'
  }
];

const InstagramImportModal = ({ isOpen, onClose, API_URL, tags, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: URL, 2: Preview, 3: Prompt, 4: İşleniyor, 5: Sonuç
  const [url, setUrl] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [transformResult, setTransformResult] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [title, setTitle] = useState('Instagram Import');

  const resetModal = () => {
    setStep(1);
    setUrl('');
    setPreviewData(null);
    setSelectedTemplate(null);
    setUserInput('');
    setCustomPrompt('');
    setIsLoading(false);
    setTransformResult(null);
    setSelectedTags([]);
    setTitle('Instagram Import');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Step 1: URL'den preview al
  const handlePreview = async () => {
    if (!url.includes('instagram.com')) {
      toast.error('Geçerli bir Instagram URL\'i girin');
      return;
    }

    setIsLoading(true);
    try {
      const response = await instagramScrapeAPI.preview(url);
      if (response.data.success) {
        setPreviewData(response.data.data);
        setStep(2);
      } else {
        toast.error('İçerik çekilemedi: ' + (response.data.error || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Preview hatası:', error);
      toast.error('İçerik çekilemedi. URL\'i kontrol edin.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Dönüştür
  const handleTransform = async () => {
    let finalPrompt = '';

    if (selectedTemplate === 'custom') {
      finalPrompt = customPrompt;
    } else if (selectedTemplate) {
      const template = TEMPLATE_PROMPTS.find(t => t.id === selectedTemplate);
      finalPrompt = template.prompt + userInput;
    }

    if (!finalPrompt.trim()) {
      toast.error('Lütfen bir prompt girin');
      return;
    }

    setIsLoading(true);
    setStep(4);

    try {
      const response = await instagramScrapeAPI.importAndTransform(url, finalPrompt);
      if (response.data.success) {
        setTransformResult(response.data.data);
        setStep(5);
        toast.success('Fotoğraf başarıyla dönüştürüldü!');
      } else {
        toast.error('Dönüştürme başarısız: ' + (response.data.error || 'Bilinmeyen hata'));
        setStep(3);
      }
    } catch (error) {
      console.error('Transform hatası:', error);
      toast.error('Dönüştürme sırasında hata oluştu');
      setStep(3);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 5: Kütüphaneye ekle
  const handleAddToLibrary = async () => {
    setIsLoading(true);
    try {
      const response = await instagramScrapeAPI.addToLibrary({
        transformedData: transformResult,
        caption: previewData?.description || '',
        title: title,
        tags: selectedTags
      });

      if (response.data.success) {
        toast.success('İçerik kütüphaneye eklendi!');
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        toast.error('Kütüphaneye eklenemedi: ' + (response.data.error || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Kütüphaneye ekleme hatası:', error);
      toast.error('Kütüphaneye eklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="instagram-import-modal">

        {/* Header */}
        <div className="iim-header">
          <h3>📸 Instagram İçerik Al</h3>
          <div className="iim-steps">
            <span className={`iim-step ${step >= 1 ? 'active' : ''}`}>1. URL</span>
            <span className="iim-step-arrow">→</span>
            <span className={`iim-step ${step >= 2 ? 'active' : ''}`}>2. Önizleme</span>
            <span className="iim-step-arrow">→</span>
            <span className={`iim-step ${step >= 3 ? 'active' : ''}`}>3. Dönüştür</span>
            <span className="iim-step-arrow">→</span>
            <span className={`iim-step ${step >= 5 ? 'active' : ''}`}>4. Kaydet</span>
          </div>
          <button className="iim-close" onClick={handleClose}>✕</button>
        </div>

        {/* Body */}
        <div className="iim-body">

          {/* Step 1: URL Giriş */}
          {step === 1 && (
            <div className="iim-step-content">
              <div className="iim-url-section">
                <label>Instagram Post URL'si</label>
                <div className="iim-url-input-wrapper">
                  <input
                    type="text"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    placeholder="https://www.instagram.com/p/..."
                    className="iim-url-input"
                    onKeyDown={e => e.key === 'Enter' && handlePreview()}
                    autoFocus
                  />
                  <button
                    onClick={handlePreview}
                    disabled={isLoading || !url}
                    className="iim-btn-primary"
                  >
                    {isLoading ? '⏳ Çekiliyor...' : '🔍 Getir'}
                  </button>
                </div>
                <p className="iim-hint">Yalnızca "fotoğraf post" linkini yapıştırın</p>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 2 && previewData && (
            <div className="iim-step-content">
              <div className="iim-preview-section">
                <div className="iim-preview-media">
                  {previewData.isVideo ? (
                    <div className="iim-video-badge">
                      <span>🎬 Video</span>
                      {previewData.thumbnail && (
                        <img src={previewData.thumbnail} alt="Video thumbnail" />
                      )}
                    </div>
                  ) : (
                    <img
                      src={previewData.imageUrl}
                      alt="Instagram post"
                      className="iim-preview-image"
                      onError={e => { e.target.src = ''; e.target.alt = 'Yüklenemedi'; }}
                    />
                  )}
                </div>
                <div className="iim-preview-info">
                  <p className="iim-caption">{previewData.description || 'Açıklama yok'}</p>
                  {previewData.likeCount > 0 && (
                    <div className="iim-stats-row">
                      <span>❤️ {previewData.likeCount?.toLocaleString()}</span>
                      {previewData.commentCount > 0 && (
                        <span>💬 {previewData.commentCount?.toLocaleString()}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="iim-actions">
                <button onClick={() => setStep(1)} className="iim-btn-secondary">← Geri</button>
                <button onClick={() => setStep(3)} className="iim-btn-primary">Dönüştürme Seçenekleri →</button>
              </div>
            </div>
          )}

          {/* Step 3: Prompt Seçimi */}
          {step === 3 && (
            <div className="iim-step-content">
              <div className="iim-prompt-section">
                <label>Ne Değiştirilsin?</label>
                <div className="iim-template-grid">
                  {TEMPLATE_PROMPTS.map(template => (
                    <button
                      key={template.id}
                      className={`iim-template-btn ${selectedTemplate === template.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setUserInput('');
                        setCustomPrompt('');
                      }}
                    >
                      {template.label}
                    </button>
                  ))}
                </div>

                {selectedTemplate && selectedTemplate !== 'custom' && (
                  <div className="iim-input-section">
                    <label>{TEMPLATE_PROMPTS.find(t => t.id === selectedTemplate)?.label}</label>
                    <input
                      type="text"
                      value={userInput}
                      onChange={e => setUserInput(e.target.value)}
                      placeholder={TEMPLATE_PROMPTS.find(t => t.id === selectedTemplate)?.placeholder}
                      className="iim-text-input"
                      autoFocus
                    />
                  </div>
                )}

                {selectedTemplate === 'custom' && (
                  <div className="iim-input-section">
                    <label>Özel Prompt</label>
                    <textarea
                      value={customPrompt}
                      onChange={e => setCustomPrompt(e.target.value)}
                      placeholder="Keep the same person and face exactly. Change..."
                      className="iim-textarea"
                      rows={3}
                      autoFocus
                    />
                  </div>
                )}
              </div>
              <div className="iim-actions">
                <button onClick={() => setStep(2)} className="iim-btn-secondary">← Geri</button>
                <button
                  onClick={handleTransform}
                  disabled={!selectedTemplate || (selectedTemplate === 'custom' ? !customPrompt.trim() : !userInput.trim())}
                  className="iim-btn-primary"
                >
                  🎨 Dönüştür
                </button>
              </div>
            </div>
          )}

          {/* Step 4: İşleniyor */}
          {step === 4 && (
            <div className="iim-step-content iim-processing">
              <div className="iim-spinner"></div>
              <h4>AI Dönüşümü Yapılıyor...</h4>
              <p>Bu işlem 20-30 saniye sürebilir</p>
              <div className="iim-progress-steps">
                <span className="done">✅ Instagram'dan indirildi</span>
                <span className="active">⏳ AI ile dönüştürülüyor...</span>
                <span className="pending">⬜ Sonuç hazırlanıyor</span>
              </div>
            </div>
          )}

          {/* Step 5: Sonuç ve Kaydet */}
          {step === 5 && transformResult && (
            <div className="iim-step-content">
              <div className="iim-result-section">
                <div className="iim-comparison">
                  <div className="iim-compare-item">
                    <span className="iim-compare-label">Orijinal</span>
                    <img
                      src={previewData?.imageUrl || `${API_URL}/uploads/${transformResult.original?.fileName}`}
                      alt="Orijinal"
                      className="iim-compare-image"
                    />
                  </div>
                  <div className="iim-compare-arrow">→</div>
                  <div className="iim-compare-item">
                    <span className="iim-compare-label">Dönüştürülmüş</span>
                    <img
                      src={`${API_URL}/uploads/${transformResult.transformed?.fileName}`}
                      alt="Dönüştürülmüş"
                      className="iim-compare-image"
                    />
                  </div>
                </div>

                {/* Başlık */}
                <div className="iim-input-section">
                  <label>İçerik Başlığı</label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="iim-text-input"
                    placeholder="Başlık girin..."
                  />
                </div>

                {/* Etiketler */}
                {tags && tags.length > 0 && (
                  <div className="iim-tags-section">
                    <label>Etiketler</label>
                    <div className="iim-tags-list">
                      {tags.map(tag => (
                        <button
                          key={tag.id}
                          className={`iim-tag ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                          onClick={() => toggleTag(tag.id)}
                          style={{
                            backgroundColor: selectedTags.includes(tag.id) ? tag.color : 'transparent',
                            borderColor: tag.color,
                            color: selectedTags.includes(tag.id) ? '#fff' : tag.color
                          }}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="iim-actions">
                <button onClick={() => { setStep(3); setTransformResult(null); }} className="iim-btn-secondary">
                  🔄 Tekrar Dönüştür
                </button>
                <button
                  onClick={handleAddToLibrary}
                  disabled={isLoading}
                  className="iim-btn-success"
                >
                  {isLoading ? '⏳ Kaydediliyor...' : '💾 Kütüphaneye Ekle'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstagramImportModal;